// =====================================================================
// Alerts — Sprint 3 Phase 3G-i (2026-05-31)
// =====================================================================
// Zod mirror of `crates/rhia-protocol/src/alerts.rs`. See
// `docs/superpowers/specs/2026-05-31-phase-3g-alert-pipeline-decisions.md`
// for the 20 decisions that shaped this.
// =====================================================================

import { z } from 'zod';

export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type ParsedAlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const AlertOperatorSchema = z.enum(['lt', 'lte', 'gt', 'gte', 'eq', 'not_eq']);
export type ParsedAlertOperator = z.infer<typeof AlertOperatorSchema>;

export const AlertAckStateSchema = z.enum(['unacked', 'acked', 'assigned', 'resolved']);
export type ParsedAlertAckState = z.infer<typeof AlertAckStateSchema>;

export const AlertAuditKindSchema = z.enum([
  'triggered',
  'cleared',
  'notified',
  'acked',
  'assigned',
  'resolved',
  'reopened',
]);
export type ParsedAlertAuditKind = z.infer<typeof AlertAuditKindSchema>;

// ── Recursive condition tree ─────────────────────────────────────────
// Discriminated union TS type + zod recursion via z.lazy. Editor
// consumers narrow via `switch(c.kind)` and get the right shape.
// `clear_value` allows `undefined` so the inferred type from
// `.nullable().optional()` lines up under exactOptionalPropertyTypes.

export type ParsedAlertCondition =
  | {
      kind: 'threshold';
      tag_id: string;
      operator: ParsedAlertOperator;
      value: number;
      clear_value?: number | null | undefined;
    }
  | {
      kind: 'expression';
      source: string;
      primary_tag_id: string;
    }
  | { kind: 'all'; children: ParsedAlertCondition[] }
  | { kind: 'any'; children: ParsedAlertCondition[] };

export const AlertConditionSchema: z.ZodType<ParsedAlertCondition> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('threshold'),
      tag_id: z.string().min(1),
      operator: AlertOperatorSchema,
      value: z.number(),
      clear_value: z.number().nullable().optional(),
    }),
    z.object({
      kind: z.literal('expression'),
      source: z.string().min(1),
      primary_tag_id: z.string().min(1),
    }),
    z.object({
      kind: z.literal('all'),
      children: z.array(AlertConditionSchema).min(1),
    }),
    z.object({
      kind: z.literal('any'),
      children: z.array(AlertConditionSchema).min(1),
    }),
  ]),
);

export const QuietHoursSchema = z.object({
  start_hour: z.number().int().min(0).max(23),
  end_hour: z.number().int().min(0).max(23),
  timezone: z.string().min(1),
});
export type ParsedQuietHours = z.infer<typeof QuietHoursSchema>;

export const AlertRuleSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  severity: AlertSeveritySchema,
  condition: AlertConditionSchema,
  debounce_ms: z.number().int().nonnegative().nullable(),
  quiet_hours: QuietHoursSchema.nullable(),
  notification_channel_ids: z.array(z.string()),
  derived_from_channel_id: z.string().nullable(),
  enabled: z.boolean(),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});
export type ParsedAlertRule = z.infer<typeof AlertRuleSchema>;

export const CreateAlertRuleSchema = AlertRuleSchema.omit({
  id: true,
  created_at_ms: true,
  updated_at_ms: true,
  derived_from_channel_id: true,
});
export type ParsedCreateAlertRule = z.infer<typeof CreateAlertRuleSchema>;

export const UpdateAlertRuleSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  severity: AlertSeveritySchema.optional(),
  condition: AlertConditionSchema.optional(),
  debounce_ms: z.number().int().nonnegative().nullable().optional(),
  quiet_hours: QuietHoursSchema.nullable().optional(),
  notification_channel_ids: z.array(z.string()).optional(),
  enabled: z.boolean().optional(),
});
export type ParsedUpdateAlertRule = z.infer<typeof UpdateAlertRuleSchema>;

// ── AlertEvent + audit ───────────────────────────────────────────────

export const AlertRuleSnapshotSchema = z.object({
  name: z.string().min(1),
  severity: AlertSeveritySchema,
});
export type ParsedAlertRuleSnapshot = z.infer<typeof AlertRuleSnapshotSchema>;

export const AlertEventSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  rule_id: z.string().min(1),
  rule_snapshot: AlertRuleSnapshotSchema,
  triggered_by_tag_id: z.string().min(1),
  triggered_value: z.string(),
  triggered_at_ms: z.number().int().nonnegative(),
  cleared_at_ms: z.number().int().nullable(),
  ack_state: AlertAckStateSchema,
  acked_by_uid: z.string().nullable(),
  acked_at_ms: z.number().int().nullable(),
  ack_note: z.string().nullable(),
  assigned_to_uid: z.string().nullable(),
  assigned_at_ms: z.number().int().nullable(),
  resolved_by_uid: z.string().nullable(),
  resolved_at_ms: z.number().int().nullable(),
  resolution_note: z.string().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});
export type ParsedAlertEvent = z.infer<typeof AlertEventSchema>;

/** Body for POST /alert-events/{id}/transition — full lifecycle (Q9).
 *  Allowed transitions enforced server-side. */
export const AlertEventTransitionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ack'), note: z.string().nullable().optional() }),
  z.object({ kind: z.literal('assign'), to_uid: z.string().min(1) }),
  z.object({ kind: z.literal('resolve'), note: z.string().nullable().optional() }),
  z.object({ kind: z.literal('reopen'), note: z.string().nullable().optional() }),
]);
export type ParsedAlertEventTransition = z.infer<typeof AlertEventTransitionSchema>;

export const AlertEventAuditEntrySchema = z.object({
  id: z.string().min(1),
  event_id: z.string().min(1),
  kind: AlertAuditKindSchema,
  actor_uid: z.string().nullable(),
  at_ms: z.number().int().nonnegative(),
  details: z.string(),
});
export type ParsedAlertEventAuditEntry = z.infer<typeof AlertEventAuditEntrySchema>;
